import { useLocation } from "wouter";
import { parseLocaleFromPath, buildLocalizedPath, DEFAULT_LOCALE, type Locale } from "@/lib/i18n/locales";
import { STRINGS } from "@/lib/i18n";

/**
 * Reads the current locale from the URL and returns the matching strings
 * dictionary, plus a helper to build a link to the current page in a
 * different locale (what the language switcher uses).
 *
 * Note on scope: Home, tool pages, Contact, category pages, the full
 * tools listing, solutions/personas, blog, FAQ, and corporate-safe-tools
 * are all registered as locale-prefixed routes. A few lower-traffic
 * pages (pricing, profile, privacy, terms, about) aren't yet
 * -- those still revert to English if clicked from a translated page.
 */
export function useLocale() {
  const [location] = useLocation();
  const { locale, rest } = parseLocaleFromPath(location);
  const strings = STRINGS[locale] || STRINGS[DEFAULT_LOCALE];

  const pathInLocale = (targetLocale: Locale) => buildLocalizedPath(targetLocale, rest);

  // For internal links to OTHER pages while staying in the current
  // locale (e.g. a tool card on the Hindi homepage linking to a tool
  // page) -- distinct from pathInLocale, which changes locale while
  // staying on the same page. Only meaningful for routes that actually
  // have a locale-prefixed variant registered in App.tsx. Passing a path
  // outside that set still returns a technically-valid string, but it
  // won't match any route.
  const localizedHref = (path: string) => buildLocalizedPath(locale, path);

  return { locale, strings, currentPath: rest, pathInLocale, localizedHref };
}
